import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, BookOpen, Users, ArrowRight, Award, TrendingUp, Shield, Gift, Lightbulb, Target, Mail } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const STEPS = [
  { icon: Users, title: 'Join Free', desc: 'Sign up with your name and email. Get 50 free credits instantly.' },
  { icon: BookOpen, title: 'Submit Your Book', desc: 'Add your book\'s Amazon link and set a credit reward for reviewers.' },
  { icon: Star, title: 'Review & Earn', desc: 'Read and review other members\' books on Amazon. Earn credits for each verified review.' },
  { icon: TrendingUp, title: 'Boost Rankings', desc: 'More reviews = higher Amazon rankings = more sales. The cycle keeps going.' },
];

const BENEFITS = [
  { icon: Gift, title: '50 Free Credits', desc: 'Start reviewing immediately with credits on signup. Ghostwriter Pro subscribers get 100 bonus credits.' },
  { icon: Star, title: 'Earn Per Review', desc: 'Earn 10+ credits for every verified Amazon review you write. Build up your balance fast.' },
  { icon: Award, title: 'Reviewer Badges', desc: 'Climb from Bronze to Platinum reviewer status. Top reviewers get featured on the leaderboard.' },
  { icon: Shield, title: 'Verified Reviews', desc: 'All reviews are verified via Amazon review links ensuring authenticity and trust.' },
];

const GROWTH_RESOURCES = [
  { icon: Target, title: 'Outreach Templates', desc: 'Ready-to-use email and DM templates for requesting reviews from readers, book bloggers, and influencers.' },
  { icon: Mail, title: 'Email Scripts', desc: 'Proven email sequences for your launch list, ARC readers, and follow-up messages that get responses.' },
  { icon: TrendingUp, title: 'Amazon Optimization', desc: 'Step-by-step guide to optimizing your Amazon listing — keywords, categories, A+ content, and pricing strategy.' },
  { icon: Lightbulb, title: 'Promotion Playbook', desc: 'Social media templates, book trailer ideas, and launch day checklists used by bestselling indie authors.' },
];

export default function ClubLanding() {
  const [stats, setStats] = useState({ total_members: 0, total_books: 0, total_reviews: 0 });

  useEffect(() => {
    api.get('/club/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SeoHead
        title="The Authors' Review Exchange — Get More Amazon Book Reviews | Free Review Club"
        description="Join The Authors' Review Exchange, a free credit-based review club for authors. Get honest Amazon reviews for your book, boost rankings, and connect with fellow writers. Earn credits by reviewing books."
        keywords="book review exchange, get Amazon reviews, free book review club, authors review network, book review group, how to get book reviews on Amazon, increase Amazon book reviews, book review swap"
        path="/club"
        schemas={[{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "The Authors' Review Exchange",
          "url": "https://aighostwriterpro.com/club",
          "description": "A free credit-based review exchange for authors to boost Amazon book rankings through honest peer reviews"
        }]}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pt-40 md:pb-24 gold-glow noise-overlay">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4" data-testid="club-hero-label">Free Review Club</p>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6" data-testid="club-hero-title">
                The Authors' <span className="text-primary italic">Review Exchange</span>
              </h1>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                Get honest Amazon reviews for your book. Earn credits by reviewing others. Boost your rankings and sell more books — powered by a community of real authors.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/club/dashboard"
                  data-testid="club-hero-join"
                  className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide hover:bg-white hover:text-black transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                >
                  Join Free — Get 50 Credits <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/club/leaderboard"
                  data-testid="club-hero-leaderboard"
                  className="inline-flex items-center gap-2 rounded-sm px-8 py-4 border border-white/20 text-foreground font-semibold tracking-wide hover:border-primary hover:text-primary transition-all duration-300"
                >
                  Leaderboard
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-sm p-6 text-center backdrop-blur-md">
                  <p className="font-serif text-3xl text-primary" data-testid="stat-members">{stats.total_members}</p>
                  <p className="text-xs text-muted-foreground mt-1">Members</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-6 text-center backdrop-blur-md">
                  <p className="font-serif text-3xl text-primary" data-testid="stat-books">{stats.total_books}</p>
                  <p className="text-xs text-muted-foreground mt-1">Books Listed</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-6 text-center backdrop-blur-md">
                  <p className="font-serif text-3xl text-primary" data-testid="stat-reviews">{stats.total_reviews}</p>
                  <p className="text-xs text-muted-foreground mt-1">Reviews Given</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">How It Works</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Four Simple Steps to <span className="text-primary">More Reviews</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6 text-center relative" data-testid={`club-step-${i}`}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <s.icon className="w-8 h-8 text-primary mx-auto mb-4 mt-2" strokeWidth={1.5} />
                <h3 className="font-serif text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-white/[0.02]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Why Join</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Benefits of <span className="text-primary">Membership</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6 flex gap-4" data-testid={`club-benefit-${i}`}>
                <b.icon className="w-6 h-6 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="font-serif text-lg mb-1">{b.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Hub Preview */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Members Only</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">The <span className="text-primary">Growth Hub</span></h2>
            <p className="text-muted-foreground max-w-xl mx-auto mt-4 text-sm">Free resources to help you promote your book, get more reviews, and climb the Amazon charts. Available to all members.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {GROWTH_RESOURCES.map((r, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6 flex gap-4" data-testid={`growth-resource-${i}`}>
                <r.icon className="w-6 h-6 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="font-serif text-lg mb-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/club/dashboard" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="growth-hub-link">
              Join to unlock the Growth Hub <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">Ready to <span className="text-primary">Boost Your Book?</span></h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">Join The Authors' Review Exchange today. It's free, takes 30 seconds, and you'll start with 50 credits to get your first reviews.</p>
          <Link
            to="/club/dashboard"
            data-testid="club-cta-join"
            className="inline-flex items-center gap-2 rounded-sm px-10 py-4 bg-primary text-primary-foreground font-semibold tracking-wide hover:bg-white hover:text-black transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)]"
          >
            Join Free Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
